<!-- Goals view — OKR-style goals with progress rings -->
<script lang="ts">
  import { pmStore } from '../stores/pm.svelte.ts';
  import { api } from '../api.ts';

  interface Goal {
    id: string; workspace_id: string; title: string;
    description: string | null; target_pct: number;
    current_pct: number; due_date: string | null;
    created_at: string; updated_at: string;
  }

  let goals = $state<Goal[]>([]);
  let showNewGoal = $state(false);
  let newTitle = $state('');
  let newDesc = $state('');
  let creating = $state(false);
  let newTitleInput: HTMLInputElement | null = $state(null);

  $effect(() => {
    const wsId = pmStore.activeWorkspaceId;
    if (!wsId) return;
    api<Goal[]>(`/api/v1/workspaces/${wsId}/goals`).then(g => { goals = g; }).catch(() => {});
  });

  $effect(() => {
    if (showNewGoal) newTitleInput?.focus();
  });

  async function createGoal() {
    if (!newTitle.trim() || !pmStore.activeWorkspaceId) return;
    creating = true;
    try {
      const g = await api<Goal>(`/api/v1/workspaces/${pmStore.activeWorkspaceId}/goals`, {
        method: 'POST',
        body: JSON.stringify({ title: newTitle.trim(), description: newDesc.trim() || null }),
      });
      goals = [...goals, g];
      newTitle = ''; newDesc = ''; showNewGoal = false;
    } finally { creating = false; }
  }

  // Progress ring SVG calculation
  function progressRing(pct: number, r = 20): { strokeDash: string; offset: number } {
    const circ = 2 * Math.PI * r;
    return { strokeDash: `${circ} ${circ}`, offset: circ - (pct / 100) * circ };
  }

  function progressColor(pct: number): string {
    if (pct >= 80) return 'oklch(0.80 0.26 310)';
    if (pct >= 50) return '#ffd93d';
    if (pct >= 25) return 'oklch(0.78 0.16 85)';
    return 'oklch(0.62 0.26 25)';
  }
</script>

<div class="goals-view">
  <header class="view-header">
    <h2>Goals</h2>
    <button class="new-btn" onclick={() => { showNewGoal = true; }}>+ New Goal</button>
  </header>

  {#if showNewGoal}
    <form class="new-goal-form" onsubmit={(e) => { e.preventDefault(); createGoal(); }}>
      <input bind:this={newTitleInput} bind:value={newTitle} placeholder="Goal title (e.g. Ship v2.0 by Q2)" class="goal-input" disabled={creating} />
      <textarea bind:value={newDesc} placeholder="Description (optional)" class="goal-desc" rows={2} disabled={creating}></textarea>
      <div class="form-actions">
        <button type="submit" class="btn-create" disabled={creating || !newTitle.trim()}>Create Goal</button>
        <button type="button" class="btn-cancel" onclick={() => { showNewGoal = false; newTitle = ''; newDesc = ''; }}>Cancel</button>
      </div>
    </form>
  {/if}

  {#if goals.length === 0 && !showNewGoal}
    <div class="empty-state">
      <p>No goals yet. Goals help you track high-level progress across projects.</p>
      <button class="new-btn" onclick={() => { showNewGoal = true; }}>+ Create first goal</button>
    </div>
  {:else}
    <div class="goals-grid">
      {#each goals as goal (goal.id)}
        {@const ring = progressRing(goal.current_pct)}
        {@const color = progressColor(goal.current_pct)}
        <div class="goal-card">
          <div class="goal-ring">
            <svg width="52" height="52" viewBox="0 0 52 52">
              <circle cx="26" cy="26" r="20" fill="none" stroke="rgba(255,255,255,0.06)" stroke-width="4"/>
              <circle
                cx="26" cy="26" r="20" fill="none"
                stroke={color} stroke-width="4"
                stroke-dasharray={ring.strokeDash}
                stroke-dashoffset={ring.offset}
                stroke-linecap="round"
                transform="rotate(-90 26 26)"
                style="transition: stroke-dashoffset 600ms ease"
              />
              <text x="26" y="30" text-anchor="middle" font-size="10" fill={color} font-family="JetBrains Mono, monospace">{goal.current_pct}%</text>
            </svg>
          </div>
          <div class="goal-info">
            <div class="goal-title">{goal.title}</div>
            {#if goal.description}
              <div class="goal-desc-text">{goal.description}</div>
            {/if}
            {#if goal.due_date}
              <div class="goal-due">Due {new Date(goal.due_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</div>
            {/if}
          </div>
          <div class="goal-progress-control">
            <input
              type="range" min="0" max="100" step="5"
              value={goal.current_pct}
              class="progress-slider"
              style="accent-color: {color}"
              onchange={async (e) => {
                const pct = parseInt((e.target as HTMLInputElement).value);
                const updated = await api<Goal>(`/api/v1/goals/${goal.id}/progress`, {
                  method: 'PATCH',
                  body: JSON.stringify({ current_pct: pct }),
                }).catch(() => null);
                if (updated) goals = goals.map(g => g.id === updated.id ? updated : g);
              }}
            />
          </div>
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
.goals-view { flex: 1; overflow-y: auto; padding: 1.5rem 1.75rem; max-width: 900px; width: 100%; margin: 0 auto; box-sizing: border-box; }
.view-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 1.25rem; }
.view-header h2 { margin: 0; font-size: 1.125rem; color: #e8eaf0; font-weight: 600; }
.new-btn { background: oklch(0.72 0.28 310 / 0.10); border: 1px solid oklch(0.72 0.28 310 / 0.26); color: oklch(0.82 0.26 310 / 0.78); padding: 0.35rem 0.875rem; border-radius: 5px; cursor: pointer; font-size: 0.78rem; transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease; }
.new-btn:hover { background: oklch(0.72 0.28 310 / 0.17); color: oklch(0.80 0.26 310); }
.new-goal-form { display: flex; flex-direction: column; gap: 0.5rem; background: rgba(255,255,255,0.025); border: 1px solid oklch(0.72 0.28 310 / 0.18); border-radius: 8px; padding: 1rem; margin-bottom: 1rem; }
.goal-input, .goal-desc { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); color: #d0d4e0; padding: 0.5rem 0.75rem; border-radius: 5px; font-size: 0.85rem; outline: none; width: 100%; box-sizing: border-box; }
.goal-input:focus, .goal-desc:focus { border-color: oklch(0.72 0.28 310 / 0.32); }
.goal-desc { resize: vertical; font-family: system-ui, sans-serif; }
.form-actions { display: flex; gap: 0.5rem; }
.btn-create { background: oklch(0.72 0.28 310 / 0.12); border: 1px solid oklch(0.72 0.28 310 / 0.32); color: oklch(0.80 0.26 310); padding: 0.35rem 1rem; border-radius: 4px; cursor: pointer; font-size: 0.78rem; }
.btn-cancel { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); color: rgba(255,255,255,0.35); padding: 0.35rem 0.75rem; border-radius: 4px; cursor: pointer; font-size: 0.78rem; }
.empty-state { display: flex; flex-direction: column; align-items: center; gap: 0.75rem; padding: 3rem 0; color: rgba(255,255,255,0.2); font-size: 0.875rem; text-align: center; }
.goals-grid { display: grid; gap: 0.75rem; }
.goal-card { display: flex; align-items: center; gap: 1rem; background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.06); border-radius: 8px; padding: 0.875rem 1rem; transition: border-color 150ms ease; }
.goal-card:hover { border-color: rgba(255,255,255,0.1); }
.goal-ring { flex-shrink: 0; }
.goal-info { flex: 1; min-width: 0; }
.goal-title { font-size: 0.875rem; color: #d0d4e0; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.goal-desc-text { font-size: 0.72rem; color: rgba(255,255,255,0.3); margin-top: 0.2rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.goal-due { font-size: 0.68rem; color: rgba(255,180,50,0.5); margin-top: 0.25rem; }
.goal-progress-control { flex-shrink: 0; width: 120px; }
.progress-slider { width: 100%; cursor: pointer; }
</style>
